'use client';
import { LayoutProps } from '@/components/content/MerchandisingAssociation/parts/LayoutProps';
import { ResponsiveLayout } from '@/components/content/MerchandisingAssociation/parts/ResponsiveLayout';
import { MAX_QTY, MIN_QTY } from '@/components/content/MerchandisingAssociation/parts/constants';
import { styles } from '@/components/content/MerchandisingAssociation/styles/style';
import { useBreadcrumbTrail } from '@/data/Content/BreadcrumbTrail';
import { useDefaultStore } from '@/data/Content/DefaultStore';
import { useInventoryV2 } from '@/data/Content/InventoryV2';
import { useMerchandisingAssociation } from '@/data/Content/MerchandisingAssociation';
import { useProduct } from '@/data/Content/Product';
import { useProducts } from '@/data/Content/Products';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { DATA_KEY_PRODUCT } from '@/data/constants/dataKey';
import { ITEM_LIST_IDS } from '@/data/constants/gtm';
import { INVENTORY_STATUS } from '@/data/constants/inventory';
import { TYPES } from '@/data/constants/product';
import { ContentProvider } from '@/data/context/content';
import { useEmarsys } from '@/data/context/emarsysCtx';
import { EventsContext } from '@/data/context/events';
import { useStoreIDS } from '@/data/state/useStoreIDs';
import { GTMContainerListType } from '@/data/types/GTM';
import { ProductType } from '@/data/types/Product';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { WidgetComponentProps } from '@/data/types/Slot';
import { isValidBundleComponent } from '@/utils/getBundleComponents';
import { Box, Paper, Typography } from '@mui/material';
import { isBuyableProduct } from 'presentation/events/utils/productUtils';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
	ENABLE_EMARSYS_IDENTIFIER_FIELD,
	ENABLE_EMARSYS_TEST_MODE,
} from 'shared/custom/constants/commonConstants';
import { INVENTORY_SEARCH_PDP } from 'shared/custom/constants/inventoryConstants';
import { EmarsysResponseType } from 'shared/custom/types/emarsys';
import { URLS_CONTAINER_PAGES } from 'shared/custom/utils/emarsys';

const TEXT_COMPARE = 'Comparar';
const TEXT_SERVICE_INSTALLATION = 'SI';

const EMPTY_MERCHASSOCS: ProductType[] = [];

export const MerchandisingAssociation: FC<WidgetComponentProps> = ({ id }) => {
	const { BuyTogether } = useLocalization('MerchandisingAssociation');
	// buyableProduct is used because validate attributes to know if it's an available sku
	const { buyableProduct } = useProduct({ id: id.toString() });
	const { breadcrumb } = useBreadcrumbTrail();
	const { merchAssocs = EMPTY_MERCHASSOCS, loading } = useMerchandisingAssociation(id);
	const localization = useLocalization('productDetail');
	const { onItemListView } = useContext(EventsContext);
	const { settings } = useSettings();
	const [seconds, setSeconds] = useState<string>('');
	const { state, dispatch } = useEmarsys();
	const [emarsysPartNumbers, setEmarsysPartNumbers] = useState<string[]>([]);
	const [merchAssocPartNumbers, setMerchAssocPartNumbers] = useState<string[]>([]);
	const { user } = useUser();
	const { physicalStoreId, uniqueID } = useStoreIDS();
	const { storeData } = useDefaultStore();
	const marketId = storeData?.marketId ?? '';
	const userLoggedIn = user?.isLoggedIn ?? false;
	const userId = user?.userId;
	const email = user?.email;
	// This call is to get the products to be used but using the partNumber request data.
	const { products: merchAssocProducts, loading: loadingMerchAssocProducts } = useProducts({
		id: emarsysPartNumbers.length > 0 ? emarsysPartNumbers : merchAssocPartNumbers,
		condition: emarsysPartNumbers.length > 0 || merchAssocPartNumbers.length > 0,
	});
	// Those items are used to be called by partNumber API
	const filteredMerchAssocs = useMemo(
		() =>
			merchAssocs.filter(
				(ma) =>
					ma.productPrice.offer &&
					ma.type === TYPES.sku &&
					ma?.associationType !== TEXT_COMPARE &&
					!ma?.partNumber.includes(TEXT_SERVICE_INSTALLATION) &&
					isBuyableProduct({
						productAttributes: ma.attributes,
						physicalStoreId,
					})
			),
		[merchAssocs, physicalStoreId]
	);

	const { availability: pdpAvailability } = useInventoryV2({
		productIds: buyableProduct?.id,
		physicalStoreIds: [uniqueID],
		search: INVENTORY_SEARCH_PDP,
	});

	useEffect(() => {
		const secondsNow = Math.floor(Date.now() / 1000).toString();
		setSeconds(secondsNow);
		return () => {
			setSeconds('');
		};
	}, []);

	const availableQuantity = useMemo(() => {
		const storeAvail = pdpAvailability?.find(
			(item: ProductAvailabilityData) =>
				item.productId === buyableProduct?.id &&
				item.physicalStoreName === physicalStoreId &&
				item.inventoryStatus !== INVENTORY_STATUS.UNAVAILABLE
		);

		if (!storeAvail) return 0;

		return storeAvail.x_AvailableQuantityInMarket
			? Number(storeAvail.x_AvailableQuantityInMarket)
			: storeAvail.x_customField2
			? Number(storeAvail.x_customField2)
			: 0;
	}, [pdpAvailability, buyableProduct?.id, physicalStoreId]);

	useEffect(() => {
		if (buyableProduct && filteredMerchAssocs.length > 0 && availableQuantity > 0) {
			const partNumbers = filteredMerchAssocs.map((p) => p && p.partNumber);
			setMerchAssocPartNumbers(partNumbers);
		}
	}, [buyableProduct, filteredMerchAssocs, availableQuantity]);
	const sha256value = (userIdVal?: string): string =>
		userIdVal ? CryptoJS.SHA256(userIdVal).toString(CryptoJS.enc.Hex) : '';
	const getProductsFromEmarsys = useCallback(async () => {
		const ScarabQueue: any = window['ScarabQueue'];
		const arrayCategories = breadcrumb.filter((e) => e.type !== DATA_KEY_PRODUCT);
		const categories = arrayCategories.map((tag) => tag.label).join('>');
		await new Promise((resolve) => setTimeout(resolve, 2000));
		ScarabQueue.push([
			'recommend',
			{
				logic: `ALSO_BOUGHT_${physicalStoreId}`,
				limit: 10,
				containerId: `emarsys_bundle_items_${seconds}`,
				success: (EmarsysData: EmarsysResponseType) => {
					if (EmarsysData?.page?.products && EmarsysData?.page?.products?.length !== 0) {
						const partNumbers: string[] = EmarsysData?.page?.products.map(
							(product) => product.item
						);

						setEmarsysPartNumbers(partNumbers);
					}
					dispatch({
						type: 'EMARSYS_PDP_TAG_FINISHED',
						payload: null,
					});
				},
			},
		]);
		ScarabQueue.push(['view', id]);
		ScarabQueue.push(['exclude', 'category', 'has', categories]);
		if (userLoggedIn) {
			if (settings.userData?.[ENABLE_EMARSYS_IDENTIFIER_FIELD] === 'true') {
				if (settings.userData?.[ENABLE_EMARSYS_TEST_MODE] === 'true') {
					ScarabQueue.push(['testMode']);
				}
				ScarabQueue.push(['setCustomerId', sha256value(userId)]);
			} else {
				ScarabQueue.push(['setEmail', email]);
			}
		}
		ScarabQueue.push(['go']);
	}, [
		id,
		breadcrumb,
		physicalStoreId,
		seconds,
		dispatch,
		userLoggedIn,
		settings.userData,
		userId,
		email,
	]);

	// get the logic from react store: filter out partnumber includes 'SI' and associationType 'Comparar'
	const finalMerchAssocsProducts = useMemo(
		() => merchAssocProducts && merchAssocProducts.filter((ma) => ma !== null),
		[merchAssocProducts]
	);

	// Availability comes from the indexed inventory already present on the product payload
	// (`inventories.M<marketId>.quantity`, requested with marketId/stLocId by `useProducts`),
	const slides = useMemo(() => {
		const merchAssocsProducts = finalMerchAssocsProducts ?? [];
		const availableProduct = merchAssocsProducts.filter(
			(product) =>
				product?.productPrice.offer && isValidBundleComponent(product, marketId, physicalStoreId)
		) as ProductType[];
		const slides = buyableProduct ? [buyableProduct, ...availableProduct] : availableProduct;

		return slides.slice(0, 3);
	}, [buyableProduct, finalMerchAssocsProducts, marketId, physicalStoreId]);

	const [checked, setChecked] = useState<Record<string, boolean>>({});
	const [qty, setQty] = useState<Record<string, number>>({});

	useEffect(() => {
		if (slides.length > 0) {
			setChecked(Object.fromEntries(slides.map((s) => [s.id, true])));
			setQty(Object.fromEntries(slides.map((s) => [s.id, 1])));
		}
	}, [slides]);

	const onToggle = useCallback((id: string, next?: boolean) => {
		setChecked((prev) => {
			const current = !!prev[id];
			const value = typeof next === 'boolean' ? next : !current;
			if (value === current) return prev;
			return {
				...prev,
				[id]: value,
			};
		});
	}, []);

	const clamp = (n: number) =>
		Math.max(MIN_QTY, Math.min(MAX_QTY, Number.isFinite(n) ? n : MIN_QTY));

	const onQtySet = useCallback((id: string, value: number) => {
		setQty((prev) => ({ ...prev, [id]: clamp(value) }));
	}, []);

	const subtotal = useMemo(
		() =>
			slides.reduce((acc, it) => {
				const id = it.id;
				const isChecked = checked[id] ?? true;
				const quantity = qty[id] ?? 1;

				return acc + (isChecked ? (it.productPrice.min ?? 0) * quantity : 0);
			}, 0),
		[slides, checked, qty]
	);

	useEffect(() => {
		setChecked((prev) => {
			const next = { ...prev };
			for (const el of slides) {
				const id = String(el.id);
				if (!(id in next)) next[id] = true;
			}
			return next;
		});
	}, [slides]);

	const LayoutP: LayoutProps = {
		slides,
		checked,
		qty,
		subtotal,
		onToggle,
		onQtySet,
		availableQuantity,
	};

	const ctxValue: GTMContainerListType = useMemo(
		() => ({
			productListData: {
				listId: ITEM_LIST_IDS.MERCHANDISING_ASSOCIATIONS,
				listName: localization.merchandisingAssociations.t(),
			},
		}),
		[localization]
	);

	useEffect(() => {
		if (filteredMerchAssocs.length) {
			onItemListView({
				gtm: {
					products: filteredMerchAssocs,
					listPageName: localization.merchandisingAssociations.t(),
					listId: ITEM_LIST_IDS.MERCHANDISING_ASSOCIATIONS,
					storeName: settings.storeName,
					settings,
				},
			});
		}
	}, [localization, merchAssocs, onItemListView, settings, filteredMerchAssocs]);
	useEffect(() => {
		if (
			buyableProduct &&
			availableQuantity > 0 &&
			(!filteredMerchAssocs || filteredMerchAssocs?.length === 0) &&
			state.emarsysPDPTagFinished === URLS_CONTAINER_PAGES.PRODUCT_PAGE &&
			breadcrumb &&
			breadcrumb.length !== 0 &&
			seconds &&
			physicalStoreId
		) {
			getProductsFromEmarsys();
		}
	}, [
		slides,
		breadcrumb,
		state.emarsysPDPTagFinished,
		seconds,
		physicalStoreId,
		filteredMerchAssocs,
		availableQuantity,
		buyableProduct,
		getProductsFromEmarsys,
	]);

	if (!buyableProduct || !seconds || loading || loadingMerchAssocProducts) return (<Box id={`emarsys_bundle_items_${seconds}`} />);

	if (!slides || slides.length < 2) return (<Box id={`emarsys_bundle_items_${seconds}`} />);

	return (
		<ContentProvider value={ctxValue}>
			<Box id={`emarsys_bundle_items_${seconds}`}>
			<Paper id="buyTogetherCard" data-nosnippet elevation={1} sx={styles.buyTogetherPaper}>
				<Typography variant="headingSegment" sx={styles.cardNameDescriptionValue}>
					{BuyTogether.t()}
				</Typography>
				{emarsysPartNumbers.slice(0, 2).map((partNumber) => (
					<span
						key={partNumber}
						data-scarabitem={partNumber}
						style={{ display: 'none' }}
						className="scarab-item"
					></span>
				))}
				<ResponsiveLayout {...LayoutP} />
			</Paper>
			</Box>
		</ContentProvider>
	);
};
